import { AddPhotoAlternate as AddPhotoIcon } from '@mui/icons-material'
import { Button, CircularProgress } from '@mui/material'
import { ChangeEvent, FC, useCallback, useRef } from 'react'
import { useNotify, useRefresh, useTranslate } from 'react-admin'

import { ALLOWED_MIME_TYPES } from '../../domain/evidenceUpload'
import type { CaptureTarget } from '../../domain/liveEvidenceCapture'
import { useLiveEvidenceUpload } from '../../hooks/useLiveEvidenceUpload'

export interface StepEvidenceUploadButtonProps {
  target: CaptureTarget
}

/**
 * Uploads images to one step of a live inspection, the backoffice's stand-in
 * for the app's capture on that step.
 *
 * On a successful capture the page's record is refetched, so a photo shows up
 * in its gallery and a seal in its gallery or among the pending seals, without
 * reloading the page. Every refusal and failure is notified with the file it
 * concerns.
 */
export const StepEvidenceUploadButton: FC<StepEvidenceUploadButtonProps> = ({ target }) => {
  const translate = useTranslate()
  const notify = useNotify()
  const refresh = useRefresh()
  const inputRef = useRef<HTMLInputElement>(null)
  const { uploadToStep, isUploading } = useLiveEvidenceUpload()

  const handleChange = useCallback(
    async (event: ChangeEvent<HTMLInputElement>) => {
      const files = Array.from(event.target.files ?? [])
      // Cleared at once, so picking the same file again fires another change.
      event.target.value = ''
      if (files.length === 0) return

      const outcome = await uploadToStep(target, files)

      if (outcome.kind === 'rejected') {
        notify(`pages.inspections.upload_evidence.${outcome.reason}`, { type: 'error', messageArgs: { name: outcome.fileName } })
        return
      }

      if (outcome.uploaded > 0) {
        refresh()
      }

      const message = outcome.failures
        .map(failure => translate('pages.inspections.upload_evidence.failed', { name: failure.fileName, message: failure.message }))
        .join('\n')
      if (message !== '') {
        notify(message, { type: 'error', multiLine: true })
      } else {
        notify(
          outcome.uploaded === 1 ? 'pages.inspections.upload_evidence.success_one' : 'pages.inspections.upload_evidence.success_many',
          {
            type: 'info',
            messageArgs: { count: outcome.uploaded },
          },
        )
      }
    },
    [notify, refresh, target, translate, uploadToStep],
  )

  const label = translate(isUploading ? 'pages.inspections.upload_evidence.uploading' : 'pages.inspections.upload_evidence.label')

  return (
    <Button
      component="label"
      variant="outlined"
      size="small"
      disabled={isUploading}
      startIcon={isUploading ? <CircularProgress size={16} color="inherit" /> : <AddPhotoIcon />}
    >
      {label}
      <input
        ref={inputRef}
        data-testid={`step-upload-input-${target.stepId}`}
        type="file"
        accept={ALLOWED_MIME_TYPES.join(',')}
        multiple
        hidden
        onChange={event => {
          void handleChange(event)
        }}
      />
    </Button>
  )
}
